import { encode } from "base-64";
import { LEVEL_ALL, LEVEL_OTHER, VIEW_ROLE } from "constants/forge";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RootState } from "redux/store";
import {
  changeForgeCameraByFloorName,
  clearForgeSelection,
  getCurrentViewer,
} from "utils/forge";
import { setFamilyInstances } from "utils/forge/viewerData";
import {
  useContextInspectionDetail,
  useInspectionDetailDispatch,
} from "../context/inspectionDetail";
import { DETAIL_MODE, Types } from "../context/inspectionDetailReducer";
import { getCombinedViewList } from "apiClient/v2/bims/bimViewApi";
import {
  setIsLoadedSheetTransformRatio,
  setLoadedFamilyInstances,
} from "redux/forgeViewerSlice";
import {
  getAECData,
  transformLevelByAecDataAndDerivative,
} from "utils/forge/forgeApiData";
import {
  calculatePositionFromSheet,
  setSheetTransformMatrix,
} from "utils/forge/forge2d";
import { get3DTo2DMatrix } from "utils/forge/aec";
import { View } from "apiClient/v2/forgeApi";
import useCheckSheet from "pages/forge-viewer/hooks/useCheckSheet";
import { Level } from "interfaces/models";
import { getDataFamilyInstance } from "utils/forge/s3Data";
import { logDev } from "utils/logs";
import { forgeApi } from "apiClient/v2";
import {
  changeSelectedViewOfGroup,
  getLastSelectedViewByGroup,
} from "models/inspection";

const useLoadForgeViewer = () => {
  const { bimId } = useParams();
  const { dataProjectDetail } = useSelector(
    (state: RootState) => state.project
  );
  const {
    isLoadedViewerModelData,
    isLoadedViewer,
    isLoadedSheetTransformRatio,
  } = useSelector((state: RootState) => state.forgeViewer);

  const { mode, forgeViewerData, inspectionGroup } =
    useContextInspectionDetail();
  const dispatch = useInspectionDetailDispatch();
  const [aecData, setAecData] = useState();
  const dispatchRedux = useDispatch();
  const lastGuidView = useRef("");

  const selectedView = useMemo(() => {
    const { views, guid } = forgeViewerData;

    return views.find((v) => v.guid === guid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forgeViewerData.guid]);

  useEffect(() => {
    const { id, version, projectId } = dataProjectDetail || {};
    if (!id || !version || !projectId || aecData || !isLoadedViewerModelData) {
      return;
    }
    dispatchRedux(setIsLoadedSheetTransformRatio(false));
    (async () => {
      const versionId = `${id}?version=${version}`;
      const [aecData, derivativeResponse] = await Promise.all([
        getAECData(versionId),
        forgeApi.getDerivativesByVersionId({
          projectId: projectId || "",
          versionId: encodeURIComponent(versionId),
        }),
      ]);
      setAecData(aecData);
      if (derivativeResponse?.data) {
        const { levels } = transformLevelByAecDataAndDerivative(
          aecData,
          derivativeResponse.data
        );
        const levelData = [{ ...LEVEL_ALL }, ...levels, { ...LEVEL_OTHER }];
        dispatch({
          type: Types.SET_FORGE_VIEWER,
          payload: { levelData },
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataProjectDetail?.id,
    dataProjectDetail?.version,
    aecData,
    isLoadedViewerModelData,
  ]);

  useEffect(() => {
    if (!isLoadedViewerModelData) {
      dispatchRedux(setLoadedFamilyInstances(false));
    }
  }, [isLoadedViewerModelData, dispatchRedux]);

  useEffect(() => {
    if (!aecData || !forgeViewerData.guid) {
      return;
    }
    if (!selectedView) {
      return;
    }
    let sheetMatrix: THREE.Matrix4 = new THREE.Matrix4();
    if (selectedView.sheetGuid) {
      const viewport = (aecData as any).viewports.find(
        (v: any) => v.sheetGuid === selectedView.sheetGuid
      );
      if (!!viewport) {
        sheetMatrix = get3DTo2DMatrix(viewport, 0.001)?.clone();
      }
    }
    setSheetTransformMatrix(sheetMatrix);
    dispatchRedux(setIsLoadedSheetTransformRatio(true));
    lastGuidView.current = forgeViewerData.guid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aecData, isLoadedViewerModelData, selectedView?.guid, dispatchRedux]);

  useEffect(() => {
    const { levelData } = forgeViewerData;

    if (
      !levelData ||
      !selectedView ||
      !selectedView.sheetGuid ||
      !isLoadedSheetTransformRatio ||
      selectedView.guid !== lastGuidView.current ||
      !isLoadedViewer ||
      !dataProjectDetail?.id
    ) {
      return;
    }
    const viewer = getCurrentViewer();
    if (!viewer) {
      return;
    }
    const currentSheet = dataProjectDetail.sheetData?.find(
      (sheet) => sheet.guid === selectedView.sheetGuid
    );
    if (currentSheet?.isMissingViewport) {
      setFamilyInstances({});
      dispatchRedux(setLoadedFamilyInstances(true));

      return;
    }
    const position = calculatePositionFromSheet(
      viewer.impl.intersectGround(0, 0)
    );
    let currentLevel = levelData.find((level: Level) => {
      return level.zMax! >= position.z && level.zMin! <= position.z;
    });
    if (!currentLevel) {
      currentLevel = LEVEL_OTHER;
    }
    (async () => {
      const familyInstances = await getDataFamilyInstance({
        bimFileId: dataProjectDetail.id,
        version: String(dataProjectDetail.version),
        level: currentLevel.label,
      });
      if (familyInstances) {
        setFamilyInstances(familyInstances);
        dispatchRedux(setLoadedFamilyInstances(true));
        logDev("Loaded family instances", currentLevel.label);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataProjectDetail?.id,
    selectedView,
    isLoadedViewer,
    forgeViewerData?.levelData,
    isLoadedSheetTransformRatio,
  ]);

  useEffect(() => {
    (async () => {
      if (!bimId || !dataProjectDetail?.id) {
        return;
      }
      const urn = `${dataProjectDetail.id}?version=${dataProjectDetail.version}`;
      const versionId = encodeURIComponent(urn);
      const response = await getCombinedViewList(
        dataProjectDetail.projectId,
        versionId
      );
      if (!response?.data) {
        return;
      }
      const views = (response.data || []) as View[];
      let firstView = views.find((v) => v.role === VIEW_ROLE.ROLE_3D);
      if (!firstView) {
        firstView = views.find((v) => v.role === VIEW_ROLE.ROLE_2D);
      }
      dispatch({
        type: Types.SET_FORGE_VIEWER,
        payload: {
          views: views,
          guid: firstView?.guid,
          urn: encode(
            decodeURIComponent(`${bimId}?version=${dataProjectDetail?.version}`)
          ),
        },
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bimId, dataProjectDetail?.id, dispatch]);

  useLayoutEffect(() => {
    if (!forgeViewerData.views?.length || mode === DETAIL_MODE.ADD_PIN) {
      return;
    }
    const currentViewGuid = forgeViewerData.guid;
    const selectedGroupId = inspectionGroup?.id;
    if (!selectedGroupId) {
      return;
    }
    const lastViewGuid = selectedGroupId
      ? getLastSelectedViewByGroup(selectedGroupId)
      : null;
    let view = null;
    const views = forgeViewerData.views;
    if (lastViewGuid) {
      view = views.find((v) => v.guid === lastViewGuid);
    }

    if ((currentViewGuid === lastViewGuid || !view) && currentViewGuid) {
      changeSelectedViewOfGroup(selectedGroupId, currentViewGuid);

      return;
    }
    dispatch({
      type: Types.SET_FORGE_VIEWER,
      payload: {
        guid: view?.guid,
      },
    });
  }, [forgeViewerData.views, inspectionGroup?.id]);

  useEffect(() => {
    if (!forgeViewerData.guid) {
      return;
    }
    const selectedGroupId = inspectionGroup?.id;
    if (!selectedGroupId) {
      return;
    }
    changeSelectedViewOfGroup(selectedGroupId, forgeViewerData.guid);
  }, [forgeViewerData?.guid]);

  // TODO: update height for forge when change mode
  useLayoutEffect(() => {
    let height: string | number | undefined;

    switch (mode) {
      case DETAIL_MODE.ADD_PIN_EXIST:
        height = "calc(var(--app-height))";
        break;

      case DETAIL_MODE.DETAIL_PIN_GROUP:
        height = "calc(var(--app-height) - var(--header-height))";
        break;

      case DETAIL_MODE.ADD_PIN:
        height = "100%";
        break;

      case DETAIL_MODE.ALL:
        height = 600;
        break;

      default:
        break;
    }

    if (height) {
      dispatch({
        type: Types.SET_FORGE_VIEWER,
        payload: {
          height,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // TODO: change camera of forge by floor
  useLayoutEffect(() => {
    const currentView = forgeViewerData.views.find(
      (view) => view.guid === forgeViewerData.guid
    );
    if (!isLoadedViewerModelData || !currentView) {
      return;
    }
    const viewer = getCurrentViewer();

    if (viewer?.model?.is3d()) {
      changeForgeCameraByFloorName(currentView?.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadedViewerModelData, forgeViewerData.guid]);

  useEffect(() => {
    getCurrentViewer()?.utilities.goHome();
  }, [mode]);

  // TODO: Toggles ghosting during search and isolate
  useEffect(() => {
    const viewer = getCurrentViewer();
    if (isLoadedViewer && viewer) {
      viewer.setGhosting(true);
    }
  }, [isLoadedViewer]);

  useLayoutEffect(() => {
    return () => {
      clearForgeSelection();
    };
  }, [isLoadedViewerModelData, mode]);

  useCheckSheet({
    selectedView,
  });
};

export default useLoadForgeViewer;
